import { useMemo } from 'react';
import { EligibilityType } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/shared/enums.pb';
import { InsuranceQueries } from '@frontend/api-insurance';
import { InsuranceDetailQueries } from '@frontend/api-insurance-detail';
import { PersonHelpers } from '@frontend/api-person';
import { useTranslation } from '@frontend/i18n';
import { useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails } from '@frontend/insurance-verification';
import { useInsuranceType } from '../providers/insurance-type-provider';
import { usePersonInfoStore } from '../providers/person-info-provider';
import { OTHER_SUBSCRIBER_SELECTION_VALUE } from '../utils/constants';
import { DropdownOption } from '../utils/types';
import { getPayerCombinedId } from '../utils/utils';
import { useGetLocationVerticalForPayerList } from './use-get-location-vertical-for-payer-list';

interface UseGetFormDataReturnType {
  payerList: DropdownOption[];
  providerList: { nationalProviderId: string; taxId: string }[];
  nationalProviderIdList: string[];
  primSubscriberList: DropdownOption[];
  isLoading?: boolean;
}

export const useGetFormData = () => {
  const { t } = useTranslation('insuranceDetails');
  const { personInfo, locationId } = usePersonInfoStore();
  const { insuranceType } = useInsuranceType();
  const personId = personInfo?.PersonID as string;
  const householdId = personInfo?.WeaveHouseholdID as string;

  const { locationVerticalForPayerList, isDental, isWellnessVertical } = useGetLocationVerticalForPayerList(
    locationId || ''
  );

  const { locationIdWiseCustomizationFlagDetails } = useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails();

  const isEnhancedInsuranceVerificationEnabled = locationIdWiseCustomizationFlagDetails?.[locationId!] || false;

  const payerListQuery = InsuranceDetailQueries.useGetPayerList({
    locationId: locationId || '',
    vertical: locationVerticalForPayerList,
    eligibilityType: isEnhancedInsuranceVerificationEnabled ? EligibilityType.ENHANCED : EligibilityType.LITE,
    options: {
      enabled: !!locationId,
    },
  });
  const providerListQuery = InsuranceDetailQueries.useGetLocationNpis(locationId!, {
    enabled: !!locationId,
  });
  const personHouseholdsQuery = InsuranceDetailQueries.useGetHouseholdData(locationId!, householdId, insuranceType);

  const insuranceDetailsQuery = InsuranceDetailQueries.useGetInsuranceDetailsByPersonId({
    personId,
    locationId: locationId || '',
    options: {
      enabled: !!personId && !!locationId && isDental,
    },
  });
  const practiceTypeList = InsuranceQueries.useGetPracticeTypeList(locationId!, isWellnessVertical);

  const isLoading =
    payerListQuery.isLoading ||
    providerListQuery.isLoading ||
    personHouseholdsQuery.isLoading ||
    practiceTypeList.isLoading ||
    insuranceDetailsQuery.isLoading;

  const { payerList, providerList, nationalProviderIdList, primSubscriberList } =
    useMemo<UseGetFormDataReturnType>(() => {
      if (isLoading) {
        return { payerList: [], providerList: [], nationalProviderIdList: [], primSubscriberList: [] };
      }

      const payerList =
        payerListQuery.data?.map<DropdownOption>((item) => ({
          label: item.name,
          value: item.id,
          uniqueCombinedId: getPayerCombinedId(item.id, item.name),
          alternativePayerIds: item.alternatePayerIds || [],
        })) ?? [];
      const providerList = providerListQuery.data ?? [];
      const nationalProviderIdList = providerList.reduce<string[]>((acc, provider) => {
        return provider.nationalProviderId ? [...acc, provider.nationalProviderId] : acc;
      }, []);
      const personHouseHoldList =
        personHouseholdsQuery.data?.persons?.sort((a, b) => {
          return new Date(a.birthdate || '').getTime() - new Date(b.birthdate || '').getTime();
        }) ?? [];

      const primSubscriberList = personHouseHoldList
        .filter((p) => p.personId !== personId)
        .map<DropdownOption>((person) => ({
          label: PersonHelpers.getFullName({
            FirstName: person.firstName,
            LastName: person.lastName,
            PreferredName: person.preferredName,
          }),
          value: person.personId,
          isGuardian: person.isGuardian,
        }));

      if (primSubscriberList.length > 0) {
        primSubscriberList.push({
          label: t('Other Subscriber'),
          value: OTHER_SUBSCRIBER_SELECTION_VALUE,
        });
      }

      return { payerList, providerList, nationalProviderIdList, primSubscriberList };
    }, [isLoading, payerListQuery.data, providerListQuery.data, personHouseholdsQuery.data?.persons, personId, t]);

  return {
    isLoading,
    payerList,
    providerList,
    nationalProviderIdList,
    primSubscriberList,
    insuranceDetails: insuranceDetailsQuery.data,
    practiceTypeList: practiceTypeList.data,
  };
};
